import { type Renamed } from './DraftPersister'
import { type DraftStateSlice, type PrepareContext } from './DraftJobViewModel'
import { type StepFactory, type StepViewModel } from './OutlineViewModel'
import { action, computed, observable } from 'mobx'
import { Subject } from 'rxjs'
import { type ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'

const TITLE = 'Do you want to give this job a name?'

/**
 * Draft slice for naming the job.
 */
export class NameInputViewModel
  implements ActivationLifecycle, DraftStateSlice
{
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * The singleton step for this slice.
   */
  readonly step: StepViewModel

  /**
   * Used to emit changes so we know when to trigger a save.
   *
   * @private
   */
  private readonly _changesSubject = new Subject<Renamed>()

  /**
   * Emits a value when the name was changed by the user.
   */
  readonly changes$ = this._changesSubject.asObservable()

  /**
   * Job name.
   */
  @observable accessor name = ''

  /**
   * Constructor.
   *
   * @param createStep Factory for creating a Step.
   * @param _isDisabled Getter for whether the selector is disabled
   * @param _canEditDraftJobsAsAdmin Getter for whether the selector is visible
   */
  constructor(
    createStep: StepFactory,
    private readonly _isDisabled: () => boolean,
    private readonly _canEditDraftJobsAsAdmin: () => boolean,
  ) {
    this.step = createStep({
      stepId: 'name',
      // This is an optional field for all scenarios, if not specified, name will come
      // from legend calculation.
      isCompleted: () => true,
      outlineItemDisplay: () => {
        return {
          title: TITLE,
          value: this.name,
          visual: {
            type: 'QuestionIcon',
            questionType: 'text',
          },
        }
      },
    })
  }

  /**
   * This is the only step accessor.
   */
  @computed
  get stepAccessors() {
    return this._canEditDraftJobsAsAdmin() ? [this] : []
  }

  /**
   * Whether this control is readonly.
   */
  @computed
  get disabled() {
    return this._isDisabled()
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Hydrates state from a Job.
   */
  prepare(context: PrepareContext): Promise<void> {
    if (context.type === 'EditDraft') {
      this.setName(context.job.name)
    }
    return Promise.resolve()
  }

  /**
   * Sets the name.
   *
   * @param newName
   */
  @action.bound
  setName(newName: string) {
    this.name = newName
  }

  /**
   * Renames the job to the entered name.
   */
  @action.bound
  commitName() {
    const newName = this.name.trim()
    this.name = newName
    this._changesSubject.next({ newName, type: 'Renamed' })
  }
}
